import React from 'react';
import PropTypes from 'prop-types';
import Head from 'next/head';

import * as CustomPropTypes from 'lib/CustomPropTypes';
import { shareImage } from 'lib/social';
import { getCanonical } from 'lib/urlUtils';
import getISODate from 'lib/getISODate';
import getSocialImage from 'lib/getSocialImage';
import { isTelemundoVertical } from 'lib/vertical';

export const constructJSONLD = ({
  url = '',
  section = '',
  vertical = '',
  id = '',
}) => JSON.stringify({
  '@context': {
    '@vocab': 'http://schema.org',
    pageType: {
      '@id': 'Text',
      '@type': '@id',
    },
    vertical: {
      '@id': 'Text',
      '@type': '@id',
    },
    subVertical: {
      '@id': 'Text',
      '@type': '@id',
    },
    section: {
      '@id': 'Text',
      '@type': '@id',
    },
    subSection: {
      '@id': 'Text',
      '@type': '@id',
    },
    packageId: {
      '@id': 'Text',
      '@type': '@id',
    },
    sponsor: {
      '@id': 'Text',
      '@type': '@id',
    },
    appVersion: {
      '@id': 'Text',
      '@type': '@id',
    },
  },
  '@type': 'Dataset',
  name: 'additionalTaxonomy',
  description: 'This is additional taxonomy that helps us with analytics',
  url,
  pageType: section ? 'SectionFront' : 'Front', // need lowercase later, and try to not be hardcoded
  vertical,
  subVertical: section || '', // subvertical is always the section on the front (but not in content)
  section: '',
  subSection: '',
  packageId: id,
  sponsor: '', // no sponsored fronts I think
  appVersion: process.env.RAMEN_VERSION,
});

function FrontMetadata({
  vertical, curation, section, domain, firstContentInZoneA,
}) {
  const {
    id,
    title,
    seoTitle,
    seoDescription,
    socialImage,
    dateCreated,
    dateModified,
    pageType,
  } = curation;

  let newsVerticalName = 'NBC News';
  let twitterSite = 'NBCNews';
  if (vertical === 'today') {
    newsVerticalName = 'TODAY';
    twitterSite = 'TodayShow';
  } else if (isTelemundoVertical(vertical)) {
    newsVerticalName = 'Telemundo';
    twitterSite = 'Telemundo';
  }

  let url = `https://www.${domain}`;
  if (['news', 'msnbc', 'today', 'sponsoredcontent'].includes(vertical) === false) {
    url += `/${vertical}`;
  }
  if (section) {
    url += `/${section}`;
  }

  const headline = `${seoTitle} | ${newsVerticalName}`;

  const image = getSocialImage(socialImage);

  const isShowLead = firstContentInZoneA && firstContentInZoneA.type === 'showLead' && firstContentInZoneA.metadata;
  const showDescription = isShowLead ? firstContentInZoneA.metadata.description : null;

  return (
    <Head>
      <title>{headline}</title>
      <meta name="description" content={seoDescription} />
      {vertical === 'news' && !section && (
        <meta name="pocket-site-verification" content="7d430daabab89c549b078097430be5" />
      )}
      {vertical === 'news' && !section && (
        <meta name="google-site-verification" content="xcFg25oVUcM7Zmt3RO2DdNXUrzie1AqgHOoKA_uTeks" />
      )}
      {vertical === 'today' && !section && (
        <meta name="google-site-verification" content="CW4UdAimFXD75AQd4jFiVmY4R-gOduNQJHSJMfzr8cw" />
      )}
      {vertical === 'msnbc' && !section && (
        <meta name="google-site-verification" content="REPjLi_yDHx7TLKgH3OC5uFnLfGp1M-1m8QUec6g8qQ" />
      )}

      <link rel="canonical" href={getCanonical(url)} />

      <meta property="og:url" content={url} />
      <meta property="og:title" content={seoTitle} />
      <meta property="og:description" content={seoDescription} />
      <meta property="og:image" content={shareImage(image.url, vertical, 'og')} />
      <meta property="og:image:secure_url" content={shareImage(image.url, vertical, 'og')} />
      <meta property="og:image:type" content={image.encodingFormat || 'image/gif'} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:type" content="website" />
      <meta name="twitter:site" content={twitterSite} />
      <meta name="twitter:image:src" content={shareImage(image.url, vertical, 'twitter')} />
      <meta name="twitter:title" content={seoTitle || title} />
      <meta name="twitter:description" content={seoDescription} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:image" content={shareImage(image.url, vertical, 'twitter')} />
      <meta name="thumbnail" content={shareImage(image.url, vertical)} />
      <script type="application/ld+json" data-test="front-jsonld">
        {constructJSONLD({
          url, section, vertical, id,
        })}
      </script>
      {pageType !== 'showPage' ? (
        <script type="application/ld+json" data-test="jsonld__WebSite">
          {JSON.stringify(
            {
              '@context': 'http://schema.org',
              '@type': 'WebSite',
              url,
              identifier: { // not sure this should be included
                '@type': 'PropertyValue',
                propertyID: 'Document Id',
                value: id,
              },
              headline: title,
              alternativeHeadline: seoTitle,
              dateCreated: getISODate(dateCreated),
              datePublished: getISODate(dateCreated),
              dateModified: getISODate(dateModified),
              potentialAction: {
                '@type': 'SearchAction',
                target: `http://www.${domain}/search?q={search_term_string}`,
                'query-input': 'required name=search_term_string',
              },
              image: image.url ? {
                '@type': 'ImageObject',
                url: image.url,
                width: image.width,
                height: image.height,
              } : '',
              publisher: {
                '@type': 'Organization',
                name: newsVerticalName,
                logo: {
                  '@type': 'ImageObject',
                  url: 'https://nodeassets.nbcnews.com/cdnassets/projects/site-images/nbcnews-logo-white.png',
                  width: 166,
                  height: 24,
                },
              },
              description: seoDescription,
              mainEntityOfPage: {
                '@type': 'WebPage',
                inLanguage: 'English',
                datePublished: dateCreated,
                name: domain,
                '@id': url,
              },
            },
          )}
        </script>
      ) : (
        <script type="application/ld+json" data-test="jsonld__TVSeries">
          {JSON.stringify(
            {
              '@context': 'http://schema.org',
              '@type': 'TVSeries',
              url,
              productionCompany: vertical,
              temporalCoverage: showDescription,
              description: seoDescription,
              thumbnailUrl: shareImage(image.url, vertical),
            },
          )}
        </script>
      )}
    </Head>
  );
}

FrontMetadata.propTypes = {
  curation: CustomPropTypes.curation.isRequired,
  firstContentInZoneA: CustomPropTypes.package.isRequired,
  vertical: PropTypes.string.isRequired,
  section: PropTypes.string.isRequired,
  domain: PropTypes.string.isRequired,
};


export default FrontMetadata;
